import { getRobotIconName, loadAllRobotImages, loadRobotImage } from '../RobotOnMapEditable/robotMapUtils'
import { Feature, Polygon } from 'geojson'
import { useEffect, useRef, useState } from 'react'
import { FillLayer, Layer, Source, SymbolLayer } from 'react-map-gl'
import { useDispatch, useSelector } from 'react-redux'
import { RobotActions } from 'store/actions/robot'
import { selectHighlightedFeature } from 'store/selectors/mission'
import { selectRobot } from 'store/selectors/robot'
import turf from 'turf'
import { RobotTelemetryData } from 'types/telemetry'
import WebsocketManager, { SOCKET_MESSAGES } from 'utils/WebsocketManager'
import { ROBOT_NETWORK_STATUSES, TRAJECTORY_LINE_WIDTH_IN_METERS } from 'utils/constants'
import { getYawFromQuaternion } from 'utils/getYawFromQuaternion'
import { IRobotOnMap, IRobotTelemetry } from 'utils/interfaces'
import { centerMapTo, zoomMapTo } from 'utils/mapUtils'

import CenterOnRobotButton from './CenterOnRobotButton/CenterOnRobotButton'

const layerRobotStyle: SymbolLayer = {
  id: 'robot-image-style',
  type: 'symbol',
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-anchor': 'center',
    'icon-rotation-alignment': 'map',
    'icon-size': ['interpolate', ['exponential', 2], ['zoom'], 15, 0.6, 22, 3.2],
    'icon-rotate': ['get', 'yaw'], // Use data-driven styling to get yaw from feature properties
    'icon-allow-overlap': true,
  },
  paint: {
    'icon-opacity': 1,
    'icon-opacity-transition': { delay: 0, duration: 0 },
  },
}

const trajectoryStyle: FillLayer = {
  id: 'trajectory-style',
  type: 'fill',
  paint: {
    'fill-color': '#9747FF',
    'fill-opacity': 0.7,
  },
}

const RobotOnMap = ({ map }: { map: mapboxgl.Map | undefined }) => {
  const robot = useSelector(selectRobot)
  const dispatch = useDispatch()

  const [robotStyle, setRobotStyle] = useState<SymbolLayer>(layerRobotStyle)
  const [robotOnMap, setRobotOnMap] = useState<IRobotOnMap>()
  const [robotZoomed, setRobotZoomed] = useState(false)
  const [robotCenteringMode, setRobotCenteringMode] = useState(true)
  const [trajectoryPolygon, setTrajectoryPolygon] = useState<Feature<Polygon> | undefined>()
  const highlightedFeature = useSelector(selectHighlightedFeature)

  // Track last WebSocket update time (client time when message was received) to prevent REST API from overwriting fresh WebSocket data
  // Using client time (when message was received) for accurate delta calculation regardless of robot/client clock sync
  const lastWebSocketUpdateTime = useRef<number>(0) // Client time (milliseconds) when last WebSocket message was received
  const previousWebSocketUpdateTime = useRef<number>(0) // Client time (milliseconds) when previous WebSocket message was received (for calculating period)
  const lastWebSocketTimestamp = useRef<number>(0) // Robot timestamp (header.stamp.sec + nsec/1e9) from last WebSocket message for detecting out-of-order messages (in seconds with nanosecond precision)
  const wsTelemetryPeriod = useRef<number>(0) // Calculated period between WebSocket messages (milliseconds), dynamically updated
  const WS_UPDATE_THRESHOLD_MS_DEFAULT = 5000 // Default threshold if period cannot be calculated (fallback)

  // Reset zoom state on component mount to ensure auto-zoom works on page refresh
  useEffect(() => {
    setRobotZoomed(false)
    // Reset WebSocket tracking refs on mount to allow REST API updates on page refresh
    lastWebSocketUpdateTime.current = 0
    previousWebSocketUpdateTime.current = 0
    lastWebSocketTimestamp.current = 0
    wsTelemetryPeriod.current = 0
  }, [])

  // When the robot is cleared from the Redux store remove the icon from the map.
  useEffect(() => {
    if (!robot.robotInfo) {
      setRobotOnMap(undefined)
    }
  }, [robot.robotInfo])

  // Initialize robot position and orientation from robotInfo.lastPosition and lastOrientation
  // This ensures the robot appears correctly on page load before WebSocket telemetry arrives
  // Also updates when lastOrientation changes (e.g., from periodic API updates in RobotView)
  // IMPORTANT: Only update from REST API if WebSocket hasn't updated recently to prevent conflicts
  // WebSocket telemetry will override this when it arrives, as it updates robotOnMap directly
  useEffect(() => {
    if (robot.robotInfo?.lastPosition && robot.robotInfo.lastPosition.coordinates) {
      // Calculate time since last WebSocket update (in milliseconds)
      // lastWebSocketUpdateTime.current stores the client time when last WebSocket message was received
      // This gives accurate delta regardless of robot/client clock synchronization
      const currentClientTime = Date.now()

      // Calculate delta: how many milliseconds have passed since last WebSocket update
      // If lastWebSocketUpdateTime is 0, it means WebSocket never sent data (or was reset)
      const timeSinceLastWsUpdate =
        lastWebSocketUpdateTime.current > 0 ? currentClientTime - lastWebSocketUpdateTime.current : Infinity // If no WebSocket data ever received, allow REST API update

      // Use dynamically calculated WebSocket telemetry period, or fallback to default threshold
      // If period is not yet calculated (0), use default threshold
      const wsUpdateThreshold =
        wsTelemetryPeriod.current > 0 ? wsTelemetryPeriod.current : WS_UPDATE_THRESHOLD_MS_DEFAULT

      // WebSocket is considered active if it updated within the calculated period (or default threshold)
      // This means: if WebSocket sent data less than one period ago (in client time), it's active
      // We ignore REST API updates if WebSocket is actively sending data (within one period)
      const isWebSocketActive = lastWebSocketUpdateTime.current > 0 && timeSinceLastWsUpdate < wsUpdateThreshold

      // Validate coordinates from REST API
      const restApiPosition = robot.robotInfo.lastPosition.coordinates
      const isValidRestApiLongitude =
        isFinite(restApiPosition[0]) && restApiPosition[0] >= -180 && restApiPosition[0] <= 180
      const isValidRestApiLatitude =
        isFinite(restApiPosition[1]) && restApiPosition[1] >= -90 && restApiPosition[1] <= 90

      if (!isValidRestApiLongitude || !isValidRestApiLatitude) {
        return
      }

      // Only update from REST API if:
      // 1. Robot position is not yet set (initial load)
      // 2. WebSocket hasn't updated recently (more than 5 seconds ago, or never updated)
      // 3. Robot is offline (networkStatus === OFFLINE)
      // Note: We check robotOnMap inside the effect, not in dependencies, to avoid unnecessary re-renders
      // and ensure auto-zoom works correctly on page refresh
      const shouldUpdateFromRestApi =
        !robotOnMap || !isWebSocketActive || robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.OFFLINE

      if (shouldUpdateFromRestApi) {
        const yaw = robot.robotInfo.lastOrientation ? getYawFromQuaternion(robot.robotInfo.lastOrientation) : 0

        const icon = getRobotIconName(robot.robotInfo.vendor, robot.robotInfo.robot_color, robot.robotInfo.robot_type)

        setRobotOnMap({
          id: 'robot_on_map',
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: robot.robotInfo.lastPosition.coordinates,
          },
          properties: {
            yaw,
            icon,
          },
        })
        // Note: icon-rotate is now data-driven (['get', 'yaw']), so no need to update robotStyle
      }
    }
  }, [robot.robotInfo?.lastPosition, robot.robotInfo?.lastOrientation, robot.robotInfo?.networkStatus])

  useEffect(() => {
    if (!robotZoomed && robotOnMap && map) {
      // Zoom to robot if telemetry received for the first time
      zoomMapTo(map, robotOnMap ? [robotOnMap] : [])
      setRobotZoomed(true)
    }
    if (robotCenteringMode && robotOnMap && map) {
      centerMapTo(map, robotOnMap ? [robotOnMap] : [])
    }
  }, [robotZoomed, robotOnMap, map, robotCenteringMode])

  useEffect(() => {
    if (highlightedFeature) {
      setRobotCenteringMode(false)
    }
  }, [highlightedFeature])

  useEffect(() => {
    loadAllRobotImages(map)
    map?.on('style.load', () => loadAllRobotImages(map))
    map?.on('dragstart', () => setRobotCenteringMode(false))
  }, [map])

  useEffect(() => {
    const callback = (data: RobotTelemetryData) => {
      try {
        if (!robot.robotInfo?.serialNumber) {
          return
        }

        // Backend sends all actual fields in each message (exclude_none=True)
        // If field is in JSON → it's actual → include it
        // If field is not in JSON → it's stale (became None) → set to undefined
        const robotBatteryPower =
          data.robot_battery !== null &&
          data.robot_battery !== undefined &&
          data.robot_battery.power !== undefined &&
          data.robot_battery.power !== null
            ? data.robot_battery.power
            : data.battery_power !== undefined && data.battery_power !== null
            ? data.battery_power
            : undefined

        const robotBattery =
          data.robot_battery !== null && data.robot_battery !== undefined
            ? {
                power: data.robot_battery.power ?? data.battery_power ?? null,
                current: data.robot_battery.current ?? null,
                charging: data.robot_battery.charging ?? null,
              }
            : data.battery_power !== undefined && data.battery_power !== null
            ? {
                power: data.battery_power,
                current: null,
                charging: null,
              }
            : undefined

        const joystickBattery =
          data.joystick_battery !== null && data.joystick_battery !== undefined
            ? {
                power: data.joystick_battery.power ?? null,
                current: data.joystick_battery.current ?? null,
                charging: data.joystick_battery.charging ?? null,
              }
            : undefined

        dispatch(
          RobotActions.setTelemetry({
            timestamp: data.header?.stamp?.sec,
            batteryPower: robotBatteryPower,
            waterTankFilling: data.water_level,
            dirtyWaterTankFilling: data.dirty_water_level,
            temperatureControlUnit: data.temperature_control_unit,
            temperatureChassis: data.temperature_chassis,
            temperatureLidar: data.temperature_lidar,
            temperatureExternal: data.temperature_external,
            trashFullness: data.garbage_level,
            locks: data.locks,
            cleaningEquipmentState: data.cleaning_equipment_state,
            statusCode: data.status_code,
            quaternion: data.odometry?.pose?.pose?.orientation,
            velocity: data.odometry?.twist?.twist?.linear?.x,
            localization_info: data.localization_info
              ? {
                  localization_status: data.localization_info.localization_status ?? false,
                  localization_mode: data.localization_info.localization_mode ?? 0,
                  imu_bias_status: data.localization_info.imu_bias_status ?? 0,
                  gnss_status: data.localization_info.gnss_status ?? 0,
                }
              : undefined,
            firmware_info: data.firmware_info
              ? {
                  current_version: data.firmware_info.current_version ?? '',
                  latest_version: data.firmware_info.latest_version ?? '',
                }
              : undefined,
            robotBattery: robotBattery ?? undefined,
            joystickBattery: joystickBattery ?? undefined,
            system_diagnostics: data.system_diagnostics
              ? {
                  level: data.system_diagnostics.level,
                  message: data.system_diagnostics.message,
                }
              : undefined,
          })
        )

        // Update robot position only if odometry data is present
        // If odometry is missing, keep the last known position
        if (data.odometry?.pose?.pose?.position) {
          const position = data.odometry.pose.pose.position

          // Validate coordinates: longitude must be in [-180, 180], latitude in [-90, 90]
          // If coordinates are out of bounds, they might be in meters (UTM/local) instead of degrees (WGS84)
          const isValidLongitude = isFinite(position.x) && position.x >= -180 && position.x <= 180
          const isValidLatitude = isFinite(position.y) && position.y >= -90 && position.y <= 90

          if (!isValidLongitude || !isValidLatitude) {
            // Don't update position if coordinates are invalid
            return
          }

          const orientation = data.odometry.pose.pose.orientation || { x: 0, y: 0, z: 0, w: 1 }
          const yaw = getYawFromQuaternion(orientation)

          const currentClientTime = Date.now()
          const previousMapPosition = robotOnMap?.geometry.coordinates
          const positionChanged =
            previousMapPosition &&
            (Math.abs(previousMapPosition[0] - position.x) > 0.0001 ||
              Math.abs(previousMapPosition[1] - position.y) > 0.0001)

          // Check for out-of-order messages: log if new timestamp is older than previous
          // Use full precision: sec + nsec/1e9 for nanosecond accuracy (ROS standard)
          let isOutOfOrder = false
          if (data.header?.stamp?.sec !== undefined) {
            const nsec = data.header.stamp.nsec || 0
            const currentTimestamp = data.header.stamp.sec + nsec / 1e9 // Full precision timestamp in seconds
            if (lastWebSocketTimestamp.current > 0 && currentTimestamp < lastWebSocketTimestamp.current) {
              isOutOfOrder = true
            }
            lastWebSocketTimestamp.current = currentTimestamp
          }

          // Update last WebSocket update time (client time when message was received) to prevent REST API from overwriting
          // Using client time (when message was received) for accurate delta calculation regardless of robot/client clock sync
          const previousWsUpdateTime = lastWebSocketUpdateTime.current

          // Calculate WebSocket telemetry period dynamically based on time between messages
          // This allows us to use the actual telemetry update frequency instead of a fixed threshold
          if (previousWsUpdateTime > 0) {
            // Calculate period as time between current and previous message
            const period = currentClientTime - previousWsUpdateTime
            // Update period only if it's reasonable (between 100ms and 30 seconds)
            // This filters out outliers (network delays, reconnections, etc.)
            if (period >= 100 && period <= 30000) {
              // Use exponential moving average to smooth out variations
              // First time: use measured period directly
              // Subsequent times: use weighted average (70% old, 30% new) for stability
              if (wsTelemetryPeriod.current === 0) {
                wsTelemetryPeriod.current = period
              } else {
                wsTelemetryPeriod.current = Math.round(wsTelemetryPeriod.current * 0.7 + period * 0.3)
              }
            }
          }

          // Update previous time before updating current time
          previousWebSocketUpdateTime.current = previousWsUpdateTime
          lastWebSocketUpdateTime.current = currentClientTime

          const icon = getRobotIconName(
            robot.robotInfo?.vendor,
            robot.robotInfo?.robot_color,
            robot.robotInfo?.robot_type
          )

          setRobotOnMap({
            id: 'robot_on_map',
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [position.x, position.y],
            },
            properties: {
              yaw,
              icon,
            },
          })
          // Note: icon-rotate is now data-driven (['get', 'yaw']), so no need to update robotStyle

          dispatch(RobotActions.pushPositionToTrajectory([position.x, position.y]))
        }
      } catch (error) {
        console.error('[RobotOnMap] Error processing telemetry:', error)
      }
    }

    WebsocketManager.sendMessage(
      JSON.stringify({
        type: 'robot.subscribe',
        robot_serial_number: robot.robotInfo?.serialNumber,
      })
    )
    WebsocketManager.addEventListener(SOCKET_MESSAGES.ROBOT_TELEMETRY, callback)
    return () => {
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.ROBOT_TELEMETRY, callback)
      WebsocketManager.disconnect()
    }
  }, [robot.robotInfo?.serialNumber, dispatch])

  useEffect(() => {
    try {
      if (robot.trajectory.geometry.coordinates.length >= 2) {
        setTrajectoryPolygon(turf.buffer(robot.trajectory, TRAJECTORY_LINE_WIDTH_IN_METERS, 'meters'))
      } else {
        setTrajectoryPolygon(undefined)
      }
    } catch (error) {
      console.error('[RobotOnMap] Error creating trajectory polygon:', error)
    }
  }, [robot.trajectory])

  useEffect(() => {
    if (robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.OFFLINE) {
      // icon-rotate is data-driven, so we only need to update opacity
      setRobotStyle(prevStyle => ({
        ...layerRobotStyle,
        paint: {
          ...layerRobotStyle.paint,
          'icon-opacity': 0.6,
        },
      }))
    } else {
      // Reset to online style - icon-rotate is data-driven, so no need to update it
      setRobotStyle(prevStyle => ({
        ...layerRobotStyle,
      }))
    }
  }, [robot.robotInfo?.networkStatus])

  return (
    <>
      {trajectoryPolygon?.geometry.coordinates.length && robotOnMap && (
        <Source id="cleaning-route" type="geojson" data={trajectoryPolygon}>
          <Layer {...trajectoryStyle} beforeId="robot-image-style" />
        </Source>
      )}
      {robotOnMap && (
        <Source id="robot" type="geojson" data={robotOnMap}>
          <Layer {...robotStyle} />
        </Source>
      )}
      <CenterOnRobotButton onClick={() => setRobotCenteringMode(true)} />
    </>
  )
}

export default RobotOnMap
